import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Wand2, Copy, Sparkles, ArrowLeft } from "lucide-react";

// Per-tool input schema
const SCHEMAS = {
  "listing-description": [
    { key: "location", label: "Location (city, neighborhood)", type: "input" },
    { key: "property_type", label: "Property Type", type: "input" },
    { key: "beds_baths", label: "Bedrooms / Bathrooms", type: "input" },
    { key: "amenities", label: "Standout Amenities", type: "textarea" },
    { key: "vibe", label: "Vibe / Guest Persona", type: "input" },
  ],
  "title-generator": [
    { key: "property_type", label: "Property Type", type: "input" },
    { key: "location", label: "Location", type: "input" },
    { key: "highlight", label: "Hero Feature", type: "input" },
  ],
  "review-response": [
    { key: "review", label: "Paste guest review", type: "textarea" },
  ],
  "guest-message": [
    { key: "scenario", label: "Describe the scenario (check-in, late check-out, complaint, refund, upsell…)", type: "textarea" },
  ],
  "owner-pitch": [
    { key: "owner_name", label: "Owner name", type: "input" },
    { key: "property", label: "Property details", type: "textarea" },
    { key: "results", label: "Results you can promise", type: "input" },
  ],
  "facebook-post": [
    { key: "topic", label: "Topic / angle", type: "textarea" },
  ],
  "instagram-caption": [
    { key: "context", label: "What is the post about?", type: "textarea" },
  ],
  "welcome-book": [
    { key: "property", label: "Property + Location", type: "textarea" },
    { key: "special_features", label: "Anything special", type: "input" },
  ],
  "house-rules": [
    { key: "property_type", label: "Property type + guest capacity", type: "input" },
    { key: "concerns", label: "Any concerns", type: "input" },
  ],
  "complaint-response": [
    { key: "complaint", label: "Paste the complaint", type: "textarea" },
  ],
  "cleaning-checklist": [
    { key: "property", label: "Property details (rooms, features)", type: "textarea" },
  ],
  "bio-generator": [
    { key: "info", label: "Tell us about you (background, city, style)", type: "textarea" },
  ],
};

export default function AIToolDetail() {
  const { id } = useParams();
  const [tool, setTool] = useState(null);
  const [values, setValues] = useState({});
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/ai-tools").then((r) => setTool(r.data.find((t) => t.id === id)));
  }, [id]);

  const schema = SCHEMAS[id] || [{ key: "prompt", label: "Prompt", type: "textarea" }];

  const generate = async () => {
    setLoading(true); setOutput("");
    try {
      const { data } = await api.post("/ai-tools/generate", { tool_id: id, inputs: values });
      setOutput(data.output);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(typeof msg === "string" ? msg : "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const copy = () => { navigator.clipboard.writeText(output); toast.success("Copied to clipboard"); };

  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <section className="py-16 max-w-6xl mx-auto px-6 lg:px-10">
        <Link to="/ai-tools" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-brand-pink mb-8" data-testid="ai-back-link">
          <ArrowLeft className="w-4 h-4" /> All AI tools
        </Link>
        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-3">AI Tool</div>
            <h1 className="font-display text-4xl md:text-5xl leading-tight mb-4">{tool?.name || "AI Tool"}</h1>
            <p className="text-neutral-600 mb-8">Fill in the details Kristy would use in a real coaching call. The more specific, the better the output.</p>

            <div className="space-y-5">
              {schema.map((f) => (
                <div key={f.key}>
                  <Label className="mb-2 block text-sm font-medium">{f.label}</Label>
                  {f.type === "textarea" ? (
                    <Textarea
                      data-testid={`ai-input-${f.key}`}
                      value={values[f.key] || ""}
                      onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                      rows={4}
                      className="rounded-2xl border-neutral-200"
                    />
                  ) : (
                    <Input
                      data-testid={`ai-input-${f.key}`}
                      value={values[f.key] || ""}
                      onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                      className="rounded-full border-neutral-200 h-12 px-5"
                    />
                  )}
                </div>
              ))}
            </div>

            <Button
              onClick={generate}
              disabled={loading}
              size="lg"
              data-testid="ai-generate-btn"
              className="mt-8 rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white px-8 btn-pink-glow"
            >
              {loading ? <>Generating…</> : <><Wand2 className="w-4 h-4 mr-2" /> Generate</>}
            </Button>
          </div>

          <div className="lg:sticky lg:top-28">
            <div className="rounded-3xl bg-neutral-950 text-white p-8 min-h-[420px] relative">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 mb-6">
                <Sparkles className="w-3.5 h-3.5 text-brand-pink" /> Output · Claude Sonnet 4.5
              </div>
              {output ? (
                <>
                  <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-white/90">{output}</pre>
                  <button
                    onClick={copy}
                    data-testid="ai-copy-btn"
                    className="absolute top-6 right-6 inline-flex items-center gap-2 text-xs text-white/80 hover:text-brand-pink"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy
                  </button>
                </>
              ) : loading ? (
                <div className="text-white/50 text-sm">Writing… Kristy's brain is in the loop.</div>
              ) : (
                <div className="text-white/40 text-sm">Your on-brand output will appear here.</div>
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
